import { apiFetch } from '../client';

/**
 * Serie de tasas de caución diaria (Estado Financiero).
 *
 * La serie es un escalón: para una fecha vale la última tasa cargada
 * anterior o igual. Por eso no hace falta una fila por día natural, sólo
 * por rueda — y por eso el ABM es "cargar la serie", no editar fecha por
 * fecha. Backend: `server/src/routes/estadoFinanciero.ts`.
 */

export type CaucionTasa = {
  /** `yyyy-mm-dd`. */
  fecha: string;
  /** Fracción diaria del capital: 0,00074 = 0,074 %/día. NO es porcentaje. */
  tasaDiaria: number;
};

export type CaucionTasasResumen = {
  total: number;
  desde: string | null;
  hasta: string | null;
  ultimaTasa: number | null;
  recientes: CaucionTasa[];
};

export async function getCaucionTasas(): Promise<CaucionTasasResumen> {
  return apiFetch<CaucionTasasResumen>('/api/estado-financiero/caucion-tasas');
}

export async function uploadCaucionTasas(tasas: CaucionTasa[]): Promise<{ cargadas: number }> {
  return apiFetch<{ cargadas: number }>('/api/estado-financiero/caucion-tasas', {
    method: 'POST',
    body: { tasas },
  });
}

/**
 * Lee la serie desde el archivo que ya mantiene el equipo financiero.
 * Acepta los dos formatos que existen hoy:
 *  - `serie_cauciones.json`  → `{ "tasas": { "yyyy-mm-dd": 0.00074, ... } }`
 *  - `Serie_Cauciones.xlsx`  → columnas Fecha + Tasa_Diaria
 *
 * Normaliza igual que el modelo original: un valor > 0,02 se interpreta
 * como porcentaje (TNA/365 sin dividir) y se divide por 100.
 */
export async function parseCaucionFile(file: File): Promise<CaucionTasa[]> {
  const esJson = /\.json$/i.test(file.name);
  const crudas = esJson ? await parseJson(file) : await parseXlsx(file);
  return crudas
    .filter((t) => /^\d{4}-\d{2}-\d{2}$/.test(t.fecha) && Number.isFinite(t.tasaDiaria))
    .map((t) => ({ fecha: t.fecha, tasaDiaria: normalizarTasa(t.tasaDiaria) }))
    .filter((t) => t.tasaDiaria >= 0);
}

/** > 0,02 significa que vino en porcentaje; el resto ya es fracción. */
function normalizarTasa(t: number): number {
  if (!Number.isFinite(t) || t < 0) return 0;
  return t > 0.02 ? t / 100 : t;
}

async function parseJson(file: File): Promise<CaucionTasa[]> {
  const parsed: unknown = JSON.parse(await file.text());
  const tasas =
    parsed && typeof parsed === 'object' && 'tasas' in parsed
      ? (parsed as { tasas: Record<string, number> }).tasas
      : (parsed as Record<string, number>);
  return Object.entries(tasas ?? {}).map(([fecha, valor]) => ({
    fecha,
    tasaDiaria: Number(valor),
  }));
}

async function parseXlsx(file: File): Promise<CaucionTasa[]> {
  // Import perezoso: exceljs pesa y sólo hace falta al subir el archivo.
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const ws = wb.worksheets[0];
  if (!ws) return [];

  // La fila de encabezado no siempre es la primera (el export del modelo
  // antepone una nota), así que la buscamos por nombre de columna.
  let headerRow = 1;
  let colFecha = 0;
  let colTasa = 0;
  for (let r = 1; r <= Math.min(10, ws.rowCount); r++) {
    const vals = (ws.getRow(r).values as unknown[]) ?? [];
    const norm = vals.map((v) => String(v ?? '').trim().toLowerCase());
    const f = norm.findIndex((h) => h === 'fecha');
    const t = norm.findIndex((h) => h.replace(/[\s_]/g, '') === 'tasadiaria');
    if (f > 0 && t > 0) {
      headerRow = r;
      colFecha = f;
      colTasa = t;
      break;
    }
  }
  if (!colFecha || !colTasa) return [];

  const out: CaucionTasa[] = [];
  for (let r = headerRow + 1; r <= ws.rowCount; r++) {
    const row = ws.getRow(r);
    const fechaRaw = row.getCell(colFecha).value;
    const tasaRaw = row.getCell(colTasa).value;
    const fecha =
      fechaRaw instanceof Date
        ? fechaRaw.toISOString().slice(0, 10)
        : String(fechaRaw ?? '').trim().slice(0, 10);
    const tasa = Number(
      typeof tasaRaw === 'object' && tasaRaw && 'result' in tasaRaw
        ? (tasaRaw as { result: unknown }).result
        : tasaRaw,
    );
    if (fecha && Number.isFinite(tasa)) out.push({ fecha, tasaDiaria: tasa });
  }
  return out;
}
